package com.trevorwiebe.timesheet.core.domain

import kotlin.math.abs
import kotlin.math.roundToInt
import kotlin.math.roundToLong

fun Long.formatWithCommas(): String {
    val digits = toString().removePrefix("-")
    val grouped = digits.reversed().chunked(3).joinToString(",").reversed()
    return if (this < 0) "-$grouped" else grouped
}

/** Formats a cents amount as dollars with thousands separators: 2543 -> "25.43". */
fun Int.formatCentsAsDollars(): String {
    val sign = if (this < 0) "-" else ""
    val absCents = abs(this)
    val dollars = (absCents / 100).toLong().formatWithCommas()
    val cents = (absCents % 100).toString().padStart(2, '0')
    return "$sign$dollars.$cents"
}

/** Parses user-entered dollar text to cents: "25.43" -> 2543. Blank or malformed input is 0. */
fun String.dollarsToCents(): Int {
    val normalized = trim().removePrefix("$").replace(",", ".")
    val dollars = normalized.toDoubleOrNull() ?: return 0
    return (dollars * 100).roundToInt()
}

/** Cents to plain editable dollar text for seeding form fields: 2543 -> "25.43", 0 -> "". */
fun Int.centsToDollarInput(): String {
    if (this == 0) return ""
    val sign = if (this < 0) "-" else ""
    val absCents = abs(this)
    return "$sign${absCents / 100}.${(absCents % 100).toString().padStart(2, '0')}"
}

/** A percentage as short text, to two decimals at most: 93.0 -> "93", 92.5 -> "92.5". */
fun Double.formatPercent(): String {
    val hundredths = (this * 100).roundToLong()
    val whole = hundredths / 100
    val fraction = (hundredths % 100).toString().padStart(2, '0').trimEnd('0')
    return if (fraction.isEmpty()) "$whole" else "$whole.$fraction"
}

/** Parses user-entered percent text: "93" -> 93.0. Null when blank, malformed, or outside 0–100. */
fun String.toPercentOrNull(): Double? =
    trim().removeSuffix("%").trim().replace(",", ".").toDoubleOrNull()?.takeIf { it in 0.0..100.0 }
